import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { map, distinctUntilChanged, shareReplay } from "rxjs/operators";
import { Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";

export interface RestrictedModule {
  moduleId: string;
  moduleName: string;
  route: string;
  icon: string;
  isRestricted: boolean;
  blockingHoldTypes: string[];
  message: string;
}

@Injectable({
  providedIn: "root",
})
export class RestrictionService {
  // Define which hold types restrict which modules
  // These types should match what comes from your API
  private readonly restrictionMap: Record<string, string[]> = {
    Finance: [
      "applications",
      "housing",
      "student-resource",
      "student-required-forms",
      "required-forms",
      "announcements",
    ],

    Academic: [
      "applications",
      "enrollment",
      "enrollment-table",
      "enrollment-form",
      "grades",
      "graduation",
      "exam-scheduling",
    ],
    Admissions: ["applications", "enrollment", "housing"],
    Enrollment: ["enrollment", "enrollment-form", "enrollment-table"],
    Registration: ["enrollment", "enrollment-form", "enrollment-table"],
    Attendance: ["enrollment", "financial-aid"],
    Clinical: ["enrollment", "clinical-rotations"],
    Disciplinary: ["all"], // Special case - restricts EVERYTHING
    Records: ["transcripts", "graduation", "student-required-forms"],
    Graduation: ["graduation", "applications"],
    Library: ["library", "announcements"],
    Testing: ["exam-scheduling"],
    Administrative: [
      "profile",
      "settings",
      "transcripts",
      "enrollment-table",
      "enrollment-form",
      "financial-aid",
      "housing",
      "message-panel",
      "dashboard",
      "graduation",
      "announcements",
      "enrollment-table",
      "enrollment-form",
      "personal-info",
      "student-required-forms",
      "checklist",
      "checklist-form",
      "application-form",
    ],
    Immigration: ["enrollment", "international-travel"],
  };

  // All available modules in the system
  private readonly allModules: RestrictedModule[] = [
    // Dashboard / Welcome
    {
      moduleId: "dashboard",
      moduleName: "Dashboard",
      route: "/student-dashboard/welcome",
      icon: "dashboard",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },

    // Applications
    {
      moduleId: "applications",
      moduleName: "Applications",
      route: "/student-dashboard/applications",
      icon: "description",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },
    {
      moduleId: "application-form",
      moduleName: "Application Form",
      route: "/student-dashboard/application-form",
      icon: "edit_note",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },

    // Enrollment
    {
      moduleId: "enrollment",
      moduleName: "Enrollment",
      route: "/student-dashboard/enrollment-table",
      icon: "how_to_reg",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },
    {
      moduleId: "enrollment-table",
      moduleName: "Enrollment Table",
      route: "/student-dashboard/enrollment-table",
      icon: "table_rows",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },
    {
      moduleId: "enrollment-form",
      moduleName: "Enrollment Form",
      route: "/student-dashboard/enrollment-form",
      icon: "assignment",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },

    // Checklist / Forms
    {
      moduleId: "checklist-form",
      moduleName: "Checklist",
      route: "/student-dashboard/checklist",
      icon: "checklist",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },
    {
      moduleId: "checklist-form",
      moduleName: "Checklist Form",
      route: "/student-dashboard/checklist/checklist-form",
      icon: "check_box",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },

    {
      moduleId: "student-required-forms",
      moduleName: "Student Required Forms",
      route: "/student-dashboard/student-required-forms",
      icon: "assignment_turned_in",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },
    {
      moduleId: "required-forms",
      moduleName: "Required Forms",
      route: "/student-dashboard/required-forms",
      icon: "assignment_late",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },

    // Payments
    {
      moduleId: "payments",
      moduleName: "Payments",
      route: "/student-dashboard/payment-schedule",
      icon: "payments",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },
    {
      moduleId: "payment-schedule",
      moduleName: "Payment Schedule",
      route: "/student-dashboard/payment-schedule",
      icon: "schedule",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },
    {
      moduleId: "payment-history",
      moduleName: "Payment Summary",
      route: "/student-dashboard/payment-summary",
      icon: "summary",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },

    //discount-codes
    {
      moduleId: "discount-codes",
      moduleName: "Discount Codes",
      route: "/student-dashboard/discount-codes",
      icon: "redeem",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },
    {
      moduleId: "payments",
      moduleName: "Make Payment",
      route: "/student-dashboard/make-payment",
      icon: "payment",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },
    {
      moduleId: "payment-history",
      moduleName: "Payment Result",
      route: "/student-dashboard/payment-result",
      icon: "receipt",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },
    {
      moduleId: "financial-aid",
      moduleName: "Financial Aid",
      route: "/financial-aid",
      icon: "volunteer_activism",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },

    // Messages
    {
      moduleId: "messages",
      moduleName: "Messages",
      route: "/student-dashboard/message-panel",
      icon: "chat",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },
    {
      moduleId: "messages",
      moduleName: "Message Panel",
      route: "/student-dashboard/message-panel",
      icon: "forum",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },

    // Announcements
    {
      moduleId: "announcements",
      moduleName: "Announcements",
      route: "/student-dashboard/announcements",
      icon: "campaign",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },

    // Profile / Personal Info
    {
      moduleId: "profile",
      moduleName: "Profile",
      route: "/profile",
      icon: "person",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },
    {
      moduleId: "settings",
      moduleName: "Settings",
      route: "/settings",
      icon: "settings",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },
    {
      moduleId: "personal-info",
      moduleName: "Personal Info",
      route: "/profile",
      icon: "badge",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },
    //

    // Graduation
    {
      moduleId: "graduation",
      moduleName: "Graduation",
      route: "/graduation",
      icon: "school",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },

    // Transcripts
    {
      moduleId: "transcripts",
      moduleName: "Transcripts",
      route: "/transcripts",
      icon: "description",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },

    // Library
    {
      moduleId: "library",
      moduleName: "Library",
      route: "/library",
      icon: "menu_book",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },

    // Housing
    {
      moduleId: "housing",
      moduleName: "Housing",
      route: "/housing",
      icon: "home",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },

    // Exam Scheduling
    {
      moduleId: "exam-scheduling",
      moduleName: "Exam Scheduling",
      route: "/exams",
      icon: "quiz",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },
    {
      moduleId: "exams",
      moduleName: "Exams",
      route: "/exams",
      icon: "assignment",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },

    {
      moduleId: "student-resource",
      moduleName: "Student Resources",
      route: "/student-resources",
      icon: "library_books",
      isRestricted: false,
      blockingHoldTypes: [],
      message: "",
    },
  ];

  private restrictionsSubject = new BehaviorSubject<RestrictedModule[]>(
    this.allModules,
  );
  private activeHoldTypes: string[] = [];

  // Public observables
  restrictions$ = this.restrictionsSubject.asObservable().pipe(shareReplay(1));

  // Specific module observables for easy use in templates
  canAccessApplications$ = this.createModuleStream("applications");
  canAccessEnrollment$ = this.createModuleStream("enrollment");
  canAccessPayments$ = this.createModuleStream("payments");
  canAccessMessages$ = this.createModuleStream("messages");
  canAccessProfile$ = this.createModuleStream("profile");
  canAccessGraduation$ = this.createModuleStream("graduation");
  canAccessTranscripts$ = this.createModuleStream("transcripts");
  canAccessForms$ = this.createModuleStream("forms");

  // ADD THESE MISSING OBSERVABLES
  canAccessFinancialAid$ = this.createModuleStream("financial-aid");
  canAccessAnnouncements$ = this.createModuleStream("announcements");
  canAccessLMS$ = this.createModuleStream("lms");
  canAccessPromotions$ = this.createModuleStream("promotions");
  canAccessEmail$ = this.createModuleStream("email");
  canAccessHousing$ = this.createModuleStream("housing");
  canAccessLibrary$ = this.createModuleStream("library");
  canAccessExamScheduling$ = this.createModuleStream("exam-scheduling");
  canAccessChecklist$ = this.createModuleStream("checklist");
  canAccessApplicationForm$ = this.createModuleStream("application-form");
  canAccessPaymentSchedule$ = this.createModuleStream("payment-schedule");
  canAccessPaymentSummary$ = this.createModuleStream("payment-summary");
  canAccessMakePayment$ = this.createModuleStream("make-payment");
  canAccessRequiredForms$ = this.createModuleStream("required-forms");
  canAccessStudentRequiredForms$ = this.createModuleStream(
    "student-required-forms",
  );

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
  ) {
    console.log("RestrictionService initializing...");
    this.loadHoldsFromSession();
  }

  /**
   * CRITICAL: Load holds from session storage on service initialization
   * This ensures restrictions persist after page refresh
   */
  private loadHoldsFromSession(): void {
    // Get both holds and user from session
    const savedHolds = sessionStorage.getItem("studentHolds");
    const savedUser = sessionStorage.getItem("currentUser");

    console.log("Session storage check:", {
      hasHolds: !!savedHolds,
      hasUser: !!savedUser,
    });

    // Only proceed if we have both user and holds
    if (savedHolds && savedUser) {
      try {
        const holds = JSON.parse(savedHolds);
        const user = JSON.parse(savedUser);

        // Only load if user is a student and has holds
        if (user?.userType === 1 && holds.length > 0) {
          console.log("Loading saved holds from session:", holds);

          // Extract hold types
          const holdTypes = holds.map((h: any) => h.type);

          // CRITICAL: Update restrictions with the hold types
          // This will recalculate which modules should be restricted
          this.updateRestrictions(holdTypes);

          console.log("Restrictions restored after refresh");
        } else if (user?.userType === 1 && holds.length === 0) {
          console.log("ℹUser has no holds, clearing restrictions");
          this.clearRestrictions();
        }
      } catch (e) {
        console.error("Failed to parse saved holds", e);
        this.clearRestrictions();
      }
    } else {
      console.log("ℹNo saved holds found in session");

      // If user is logged in but no holds in session, clear restrictions
      if (savedUser) {
        const user = JSON.parse(savedUser);
        if (user?.userType === 1) {
          console.log(
            "User logged in but no holds in session - clearing restrictions",
          );
          this.clearRestrictions();
        }
      }
    }
  }

  private createModuleStream(moduleId: string): Observable<boolean> {
    return this.restrictions$.pipe(
      map((modules) => {
        const module = modules.find((m) => m.moduleId === moduleId);
        return module ? !module.isRestricted : true;
      }),
      distinctUntilChanged(),
    );
  }

  /**
   * Update restrictions based on hold types
   * @param activeHoldTypes Array of hold type strings
   */
  updateRestrictions(activeHoldTypes: string[]): void {
    // Store the active hold types
    this.activeHoldTypes = [...new Set(activeHoldTypes)];

    console.log("Updating restrictions based on holds:", this.activeHoldTypes);

    // Check if any hold has 'all' restriction
    const hasGlobalRestriction = this.activeHoldTypes.some((type) =>
      this.restrictionMap[type]?.includes("all"),
    );

    const updatedModules = this.allModules.map((module) => {
      // If global restriction, everything is blocked
      if (hasGlobalRestriction) {
        return {
          ...module,
          isRestricted: true,
          blockingHoldTypes: this.activeHoldTypes,
          message:
            "Your account has a disciplinary hold that restricts all access.",
        };
      }

      // Find which holds restrict this module
      const blockingHoldTypes = this.activeHoldTypes.filter((holdType) => {
        const restrictedModules = this.restrictionMap[holdType] || [];
        return restrictedModules.includes(module.moduleId);
      });

      const isRestricted = blockingHoldTypes.length > 0;

      // Generate user-friendly message
      const message = isRestricted
        ? this.generateRestrictionMessage(blockingHoldTypes, module.moduleName)
        : "";

      return {
        ...module,
        isRestricted,
        blockingHoldTypes,
        message,
      };
    });

    this.restrictionsSubject.next(updatedModules);

    const restrictedCount = updatedModules.filter((m) => m.isRestricted).length;
    console.log(`Restrictions updated: ${restrictedCount} modules restricted`);

    // Store in session for persistence across refreshes
    sessionStorage.setItem(
      "activeRestrictions",
      JSON.stringify({
        count: restrictedCount,
        types: this.activeHoldTypes,
        modules: updatedModules
          .filter((m) => m.isRestricted)
          .map((m) => m.moduleId),
        timestamp: new Date().toISOString(),
      }),
    );
  }

  /**
   * Update restrictions from hold objects (convenience method)
   * @param activeHolds Array of hold objects with type property
   */
  updateRestrictionsFromHolds(activeHolds: any[]): void {
    const holdTypes = activeHolds.map((h) => h.type);
    this.updateRestrictions(holdTypes);
  }

  private generateRestrictionMessage(
    holdTypes: string[],
    moduleName: string,
  ): string {
    if (holdTypes.length === 1) {
      return `Cannot access ${moduleName} due to ${holdTypes[0]} hold.`;
    } else {
      const lastHold = holdTypes[holdTypes.length - 1];
      const otherHolds = holdTypes.slice(0, -1).join(", ");
      return `Cannot access ${moduleName} due to ${otherHolds} and ${lastHold} holds.`;
    }
  }

  /**
   * Check if a specific module is restricted (observable)
   * @param moduleId The module ID to check
   */
  isModuleRestricted(moduleId: string): Observable<boolean> {
    return this.restrictions$.pipe(
      map((modules) => {
        const module = modules.find((m) => m.moduleId === moduleId);
        return module ? module.isRestricted : false;
      }),
    );
  }

  /**
   * Check if a module is restricted synchronously (for guards)
   * @param moduleId The module ID to check
   */
  isModuleRestrictedSync(moduleId: string): boolean {
    const module = this.restrictionsSubject.value.find(
      (m) => m.moduleId === moduleId,
    );
    return module ? module.isRestricted : false;
  }

  /**
   * Get restriction details for a module
   * @param moduleId The module ID to get details for
   */
  getModuleRestriction(
    moduleId: string,
  ): Observable<RestrictedModule | undefined> {
    return this.restrictions$.pipe(
      map((modules) => modules.find((m) => m.moduleId === moduleId)),
    );
  }

  /**
   * Get all restricted modules
   */
  getRestrictedModules(): Observable<RestrictedModule[]> {
    return this.restrictions$.pipe(
      map((modules) => modules.filter((m) => m.isRestricted)),
    );
  }

  /**
   * Get all modules with their restriction status
   */
  getAllModules(): Observable<RestrictedModule[]> {
    return this.restrictions$;
  }

  /**
   * Get current restriction state synchronously (for guards)
   */
  getCurrentRestrictions(): RestrictedModule[] {
    return this.restrictionsSubject.value;
  }

  /**
   * Check if user can access a route
   * @param url The URL to check
   */
  canAccessRoute(url: string): boolean {
    const currentRestrictions = this.restrictionsSubject.value;

    // Extract the base path (remove query params)
    const basePath = url.split("?")[0];

    // Check if this URL matches any restricted module
    for (const module of currentRestrictions) {
      if (module.isRestricted && basePath === module.route) {
        this.showAccessDenied(module);
        return false;
      }
    }
    return true;
  }

  /**
   * Show access denied message
   * @param module The restricted module
   */
  private showAccessDenied(module: RestrictedModule): void {
    this.snackBar
      .open(`🚫 Access Denied: ${module.message}`, "View Holds", {
        duration: 5000,
        panelClass: ["restriction-snackbar"],
      })
      .onAction()
      .subscribe(() => {
        this.router.navigate(["/student-dashboard/account-holds"]);
      });
  }

  /**
   * Get restrictions for a specific hold type (for display in account-holds)
   * @param holdType The hold type to get restrictions for
   */

  getRestrictionsForHoldType(
    holdType: string,
  ): { module: string; description: string }[] {
    const restrictionMap: Record<
      string,
      { module: string; description: string }[]
    > = {
      Financial: [
        { module: "Applications", description: "New applications restricted" },
        { module: "Enrollment", description: "Cannot enroll in courses" },
        { module: "Payments", description: "Payment services restricted" },
        {
          module: "Financial Aid",
          description: "Financial aid access blocked",
        },
      ],
      Academic: [
        { module: "Applications", description: "Applications on hold" },
        { module: "Enrollment", description: "Enrollment restricted" },
        { module: "Graduation", description: "Graduation application blocked" },
        { module: "Exam Scheduling", description: "Cannot schedule exams" },
      ],
      Admissions: [
        { module: "Applications", description: "New applications blocked" },
        { module: "Enrollment", description: "Enrollment on hold" },
        { module: "Housing", description: "Housing applications blocked" },
      ],
      Enrollment: [
        { module: "Enrollment", description: "Course enrollment blocked" },
        { module: "Registration", description: "Registration on hold" },
      ],
      Registration: [
        { module: "Registration", description: "Course registration blocked" },
        { module: "Enrollment", description: "Enrollment restricted" },
      ],
      Attendance: [
        { module: "Enrollment", description: "Enrollment restricted" },
        { module: "Financial Aid", description: "Financial aid on hold" },
      ],
      Clinical: [
        { module: "Enrollment", description: "Clinical enrollment blocked" },
        { module: "Clinical Rotations", description: "Rotations on hold" },
      ],
      Disciplinary: [
        {
          module: "All Services",
          description: "All student services restricted",
        },
      ],
      Records: [
        { module: "Transcripts", description: "Transcript requests blocked" },
        { module: "Graduation", description: "Graduation on hold" },
        { module: "Required Forms", description: "Forms processing delayed" },
      ],
      Graduation: [
        { module: "Graduation", description: "Graduation application blocked" },
        { module: "Transcripts", description: "Transcripts on hold" },
        { module: "Applications", description: "Applications on hold" },
      ],
      Library: [
        { module: "Library", description: "Library access restricted" },
        { module: "Announcements", description: "Announcements hidden" },
      ],
      Testing: [
        { module: "Exam Scheduling", description: "Cannot schedule exams" },
        { module: "Testing Center", description: "Testing access restricted" },
      ],
      Administrative: [
        { module: "Profile", description: "Cannot update profile" },
        { module: "Settings", description: "Settings access restricted" },
        { module: "Transcripts", description: "Transcript requests blocked" },
        { module: "Required Forms", description: "Forms submission blocked" },
        {
          module: "Personal Info",
          description: "Personal info updates blocked",
        },
      ],
      Immigration: [
        { module: "Enrollment", description: "Enrollment restricted" },
        {
          module: "International Travel",
          description: "Travel requests blocked",
        },
      ],
    };

    return (
      restrictionMap[holdType] || [
        {
          module: "Various Services",
          description: "Multiple features may be restricted",
        },
      ]
    );
  }

  /**
   * Clear all restrictions (on logout)
   */
  clearRestrictions(): void {
    this.activeHoldTypes = [];
    this.restrictionsSubject.next(
      this.allModules.map((m) => ({
        ...m,
        isRestricted: false,
        blockingHoldTypes: [],
        message: "",
      })),
    );
    sessionStorage.removeItem("activeRestrictions");
    sessionStorage.removeItem("studentHolds");
    console.log("Restrictions cleared");
  }

  /**
   * Force refresh restrictions (call after resolving holds)
   * @param holdTypes Array of hold types to apply
   */
  refreshRestrictions(holdTypes: string[]): void {
    console.log("Manually refreshing restrictions with:", holdTypes);
    this.updateRestrictions(holdTypes);
  }

  /**
   * Get current active hold types
   */
  getActiveHoldTypes(): string[] {
    return [...this.activeHoldTypes];
  }

  /**
   * Check if there are any active restrictions
   */
  hasActiveRestrictions(): boolean {
    return this.restrictionsSubject.value.some((m) => m.isRestricted);
  }

  /**
   * Get restricted modules for a specific hold type
   * @param holdType The hold type to check
   */
  getModulesRestrictedByHold(holdType: string): string[] {
    return this.restrictionMap[holdType] || [];
  }
}
